import { Badge, BadgeColor, Button } from '@odin-labs/components';
import React from 'react';
import {
  DraftTag,
  entriesFacingZone,
  entryJoinLabel,
  isDuplicateName,
  Layout,
  Zone,
} from 'siteLayout';
import { draftTagBadge } from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import { pluralize } from 'utils/pluralize';
import { NameField } from './NameField';

export type ZoneEditorProps = {
  layout: Layout;
  zone: Zone;
  tag: DraftTag;
  readOnly: boolean;
  editingDraft: boolean;
  /** The zone's Avigilon zone; absent where the revision shown says nothing about it. */
  avigilonStatus?: React.ReactNode;
  onRename: (name: string) => Promise<boolean> | void;
  onSelectEntry: (entryId: string) => void;
  onRemove: () => void;
};

const classes = {
  panel: 'odin-flex odin-flex-col odin-gap-y-5',
  heading: 'odin-flex odin-items-start odin-justify-between odin-gap-3',
  kicker:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  title:
    'odin-text-2xl odin-font-semibold odin-tracking-tight odin-text-gray-900',
  section: 'odin-flex odin-flex-col odin-gap-2',
  sectionTitle:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  empty: 'odin-text-sm odin-text-gray-500',
  entryRow:
    'odin-flex odin-items-center odin-justify-between odin-gap-3 odin-rounded-md odin-border odin-border-gray-200 odin-bg-white odin-px-3 odin-py-2 odin-text-left hover:odin-border-gray-400',
  entryName: 'odin-text-sm odin-font-medium odin-text-gray-900',
  entryJoin: 'odin-font-mono odin-text-xs odin-text-gray-500',
  footer:
    'odin-flex odin-flex-col odin-gap-2 odin-border-t odin-border-gray-200 odin-pt-5',
  note: 'odin-text-xs odin-text-gray-500',
};

export const zoneNameError = (layout: Layout, zone: Zone): string | null => {
  if (!zone.name.trim()) return 'A zone needs a name.';
  if (isDuplicateName(layout.zones, zone)) {
    return `A zone named ${zone.name.trim()} already exists in this layout.`;
  }
  return null;
};

/** The selected zone: its name, the entries that face it, and removal from the draft. */
export function ZoneEditor({
  layout,
  zone,
  tag,
  readOnly,
  editingDraft,
  avigilonStatus,
  onRename,
  onSelectEntry,
  onRemove,
}: ZoneEditorProps): React.ReactElement {
  const facing = entriesFacingZone(layout, zone.id);
  const badge = draftTagBadge(tag);
  const cannotRemove = facing.length > 0;

  return (
    <div className={classes.panel}>
      <div className={classes.heading}>
        <div>
          <p className={classes.kicker}>Zone</p>
          <h2 className={classes.title}>{zone.name || 'Unnamed zone'}</h2>
        </div>
        {badge && <Badge text={badge.text} color={badge.color as BadgeColor} />}
      </div>

      <NameField
        value={zone.name}
        error={zoneNameError(layout, zone)}
        disabled={readOnly}
        onCommit={onRename}
      />

      <section className={classes.section}>
        <h3 className={classes.sectionTitle}>
          Entries facing this zone · {facing.length}
        </h3>
        {facing.length === 0 && (
          <p className={classes.empty}>No entries face this zone.</p>
        )}
        {facing.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={classes.entryRow}
            onClick={() => onSelectEntry(entry.id)}
          >
            <span className={classes.entryName}>{entry.name}</span>
            <span className={classes.entryJoin}>
              {entryJoinLabel(layout, entry)}
            </span>
          </button>
        ))}
      </section>

      {avigilonStatus}

      {editingDraft && (
        <div className={classes.footer}>
          <div>
            <Button
              text="Remove zone from draft"
              size="sm"
              theme="danger"
              disabled={readOnly || cannotRemove}
              onClick={onRemove}
            />
          </div>
          {cannotRemove && (
            <p className={classes.note}>
              Reassign the {pluralize(facing.length, 'entry', 'entries')} that
              face this zone first:{' '}
              {facing.map((entry) => entry.name).join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
